import { useCallback, useEffect, useMemo, useState } from 'react';
import { useLocaleStore } from '@/lib/core/stores';
import { getCatalogProductById } from './catalog';
import type { FavoriteItemDto, ProductDetailDto } from '@/services/platform/api/model';

export interface FavoriteShopProduct {
  item: FavoriteItemDto;
  /** Live catalog data for `item.itemExternalId` (the product's public GUID). Undefined if the fetch failed. */
  product: ProductDetailDto | undefined;
}

export interface UseFavoriteShopProductsResult {
  data: FavoriteShopProduct[];
  isLoading: boolean;
  refetch: () => Promise<void>;
}

/**
 * Enriches Shop favorite items with live product data (vendor, price, stock), fetched in
 * parallel by `itemExternalId` — there is no batch "get products by ids" endpoint.
 */
export function useFavoriteShopProducts(items: FavoriteItemDto[]): UseFavoriteShopProductsResult {
  const locale = useLocaleStore(state => state.locale);
  const [data, setData] = useState<FavoriteShopProduct[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const itemsKey = useMemo(() => items.map(i => i.publicGuid).join(','), [items]);

  const refetch = useCallback(async () => {
    if (items.length === 0) {
      setData([]);
      return;
    }
    setIsLoading(true);
    try {
      const results = await Promise.all(
        items.map(async item => {
          if (!item.itemExternalId) return { item, product: undefined };
          try {
            const product = await getCatalogProductById(item.itemExternalId, locale);
            return { item, product };
          } catch {
            return { item, product: undefined };
          }
        })
      );
      setData(results);
    } finally {
      setIsLoading(false);
    }
    // itemsKey captures item identity changes; items itself is a new array each render.
  }, [itemsKey, locale]);

  useEffect(() => {
    refetch();
  }, [refetch]);

  return { data, isLoading, refetch };
}
